import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { games, type Game } from '../../mocks/games';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import GameIntroModal from './components/GameIntroModal';

const CARD_COLORS = [
  { border: '#f97316', bg: '#fff7ed', text: '#c2410c' },
  { border: '#10b981', bg: '#f0fdf4', text: '#065f46' },
  { border: '#ef4444', bg: '#fef2f2', text: '#991b1b' },
  { border: '#f59e0b', bg: '#fffbeb', text: '#92400e' },
  { border: '#ec4899', bg: '#fdf2f8', text: '#9d174d' },
  { border: '#84cc16', bg: '#f7fee7', text: '#3f6212' },
];

function GameCard({ game, index, onClick }: { game: Game; index: number; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  const colors = CARD_COLORS[index % CARD_COLORS.length];

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer rounded-2xl overflow-hidden flex flex-col"
      style={{
        border: `2.5px solid ${colors.border}`,
        background: '#fff',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered
          ? `0 16px 40px ${colors.border}40`
          : `0 2px 12px ${colors.border}22`,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      }}
    >
      {/* Image area */}
      <div className="relative w-full overflow-hidden" style={{ height: '200px' }}>
        <img
          src={game.image}
          alt={game.title}
          className="w-full h-full object-cover object-top transition-transform duration-500"
          style={{ transform: hovered ? 'scale(1.06)' : 'scale(1)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
        <span
          className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-sm font-bold"
          style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}55` }}
        >
          {game.category}
        </span>
      </div>

      {/* Name area */}
      <div
        className="px-5 py-4 flex flex-col flex-1 items-center justify-center text-center gap-1"
        style={{ background: colors.bg }}
      >
        <h3
          className="text-xl font-extrabold leading-tight"
          style={{ color: colors.text }}
        >
          {game.title}
        </h3>
        <p className="text-sm leading-relaxed text-primary-brown-light line-clamp-2">
          {game.shortDesc}
        </p>
        <div className="flex items-center gap-1 mt-2">
          <span
            className="flex items-center gap-1 text-sm font-semibold"
            style={{ color: colors.text }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </div>
            <span>{game.duration}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function GamesPage() {
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'Đồng đội' | 'Cá nhân'>('all');
  const navigate = useNavigate();

  const user = (() => { try { return JSON.parse(localStorage.getItem('bahnar_user') || 'null'); } catch { return null; } })();

  const filtered = games.filter(g => {
    const matchSearch = g.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = categoryFilter === 'all' || g.category === categoryFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="min-h-screen bg-primary-cream">
      <Header />

      {/* Page Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Traditional%20Bahnar%20children%20playing%20outdoors%20in%20highland%20village%2C%20tropical%20forest%20setting%2C%20warm%20afternoon%20sunlight%20filtering%20through%20trees%2C%20earthy%20green%20and%20brown%20tones%2C%20soft%20bokeh%20background%2C%20cultural%20heritage%20Vietnam%2C%20serene%20natural%20landscape%2C%20painterly%20documentary%20style&width=1400&height=400&seq=games-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
            style={{ filter: 'blur(3px) brightness(0.55)', transform: 'scale(1.05)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/72 via-black/55 to-black/35"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 text-center w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-orange/20 rounded-full mb-4 border border-primary-orange/30">
            <i className="ri-gamepad-fill text-primary-orange"></i>
            <span className="text-sm font-bold text-white/90 uppercase tracking-wider">Túi trò chơi</span>
          </div>
          <h1
            className="text-5xl font-extrabold text-white mb-4"
            style={{ textShadow: '0 2px 6px rgba(0,0,0,0.55), 0 6px 24px rgba(0,0,0,0.40), 0 0 40px rgba(180,80,0,0.3)', letterSpacing: '-0.5px' }}
          >
            Kho trò chơi dân gian Bahnar
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-6" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>
            Bấm vào từng trò chơi để xem hướng dẫn chi tiết và bắt đầu khám phá!
          </p>

          {/* Login CTA — show username if logged in, otherwise show login link */}
          {user ? (
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/25 mb-6">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-orange/30 rounded-full">
                <i className="ri-user-smile-fill text-white"></i>
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-sm">Xin chào, {user.name}! 👋</p>
                <p className="text-white/70 text-xs">Tiến độ và thành tích của bạn đang được lưu</p>
              </div>
              <button
                onClick={() => { localStorage.removeItem('bahnar_user'); window.location.reload(); }}
                className="text-white/60 hover:text-white text-xs cursor-pointer whitespace-nowrap ml-2"
              >
                Đăng xuất
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-4 px-6 py-3 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/25 mb-6">
              <div className="flex items-center gap-2 text-sm text-white/80">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-star-line text-primary-yellow"></i>
                </div>
                Đăng nhập để lưu tiến trình học của bạn
              </div>
              <Link
                to="/login"
                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-primary-orange to-primary-yellow text-white text-sm font-bold rounded-full hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-smile-fill"></i>
                Đăng nhập
              </Link>
            </div>
          )}

          {/* Search bar */}
          <div className="max-w-lg mx-auto relative">
            <div className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center">
              <i className="ri-search-line text-primary-brown-light text-base"></i>
            </div>
            <input
              type="text"
              placeholder="Tìm trò chơi..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-6 py-3 rounded-2xl border border-white/30 bg-white/90 text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-primary-brown-dark">Phân loại:</span>
          {(['all', 'Đồng đội', 'Cá nhân'] as const).map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-5 py-2 rounded-full text-base font-bold transition-all cursor-pointer whitespace-nowrap ${
                categoryFilter === cat
                  ? 'bg-primary-orange text-white'
                  : 'bg-white border-2 border-primary-orange/20 text-primary-brown-light hover:border-primary-orange hover:text-primary-orange'
              }`}
            >
              {cat === 'all' ? 'Tất cả' : (
                <>
                  <i className={`${cat === 'Đồng đội' ? 'ri-team-line' : 'ri-user-line'} mr-1`}></i>
                  {cat}
                </>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Games Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-primary-brown-light">
              <i className="ri-search-line text-5xl mb-4 block"></i>
              <p className="text-lg">Không tìm thấy trò chơi nào. Thử từ khóa khác nhé!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((game, idx) => (
                <GameCard
                  key={game.id}
                  game={game}
                  index={idx}
                  onClick={() => setSelectedGame(game)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {selectedGame && (
        <GameIntroModal
          game={selectedGame}
          onClose={() => setSelectedGame(null)}
          onStart={() => navigate(`/games/${selectedGame.id}`)}
        />
      )}
    </div>
  );
}
